import React, { useCallback, useState } from 'react';
import { X, ImagePlus, Link } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { hasProjectRoot, writeFileAsArrayBuffer, createDirectory } from '../../services/FileSystemService';
import type { SlotComponentDef, AssetFormat } from '../../../core/types/slot-asset-generator.types';
import { useAssetUrl } from '../../hooks/useAssetUrl';

const FORMAT_OPTIONS: { value: AssetFormat; label: string }[] = [
  { value: 'png_transparent', label: 'PNG (alpha)' },
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'png_keyframe', label: 'Keyframe' },
];

const REF_DIR = 'assets/slot-refs';

/**
 * Save a File to the project's assets/slot-refs/<componentId>/ directory.
 * Returns the relative path from project root, or null if no project is open.
 */
async function saveRefImageToProject(file: File, componentId: string): Promise<string | null> {
  if (!hasProjectRoot()) return null;
  const dir = `${REF_DIR}/${componentId}`;
  await createDirectory(dir, true);
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
  const safeName = file.name
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 40);
  const ts = Date.now().toString(36);
  const fileName = `${safeName}-${ts}.${ext}`;
  const filePath = `${dir}/${fileName}`;
  const buffer = await file.arrayBuffer();
  await writeFileAsArrayBuffer(filePath, buffer);
  return filePath;
}

/** Renders a ref image thumbnail from a project-relative path using useAssetUrl. */
const RefImageThumb: React.FC<{
  path: string;
  className?: string;
}> = ({ path, className }) => {
  const { url } = useAssetUrl(path);
  if (!url) return <div className={`${className} bg-zinc-800`} />;
  return <img src={url} alt="" className={className} />;
};

interface SlotComponentCardProps {
  component: SlotComponentDef;
  isSelected: boolean;
}

export const SlotComponentCard: React.FC<SlotComponentCardProps> = ({ component, isSelected }) => {
  const selectComponent = useSlotAssetGeneratorStore((s) => s.selectComponent);
  const updateComponent = useSlotAssetGeneratorStore((s) => s.updateComponent);
  const removeCustomComponent = useSlotAssetGeneratorStore((s) => s.removeCustomComponent);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = useCallback(() => {
    selectComponent(component.id);
  }, [selectComponent, component.id]);

  const stopProp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleAddRefImage = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const [handle] = await window.showOpenFilePicker({
        types: [{ description: 'Images', accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] } }],
      });
      const file = await handle.getFile();
      const savedPath = await saveRefImageToProject(file, component.id);
      if (savedPath) {
        updateComponent(component.id, {
          referenceImages: [...component.referenceImages, savedPath],
        });
      }
    } catch {
      // User cancelled picker
    }
  }, [updateComponent, component.id, component.referenceImages]);

  const handleRemoveRef = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const updated = component.referenceImages.filter((_, i) => i !== index);
    updateComponent(component.id, { referenceImages: updated });
  }, [updateComponent, component.id, component.referenceImages]);

  // --- Drag-and-drop handlers for reference image thumbnail ---

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Case 1: External file drop (from Windows Explorer)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        const savedPath = await saveRefImageToProject(file, component.id);
        if (savedPath) {
          updateComponent(component.id, {
            referenceImages: [...component.referenceImages, savedPath],
          });
        }
        return;
      }
    }

    // Case 2: Project panel asset drop — store the asset's existing relative path directly
    const assetId = e.dataTransfer.getData('text/plain');
    if (assetId) {
      const assets = useProjectPanelStore.getState().assets;
      const asset = assets[assetId];
      if (asset && asset.type === 'sprite' && asset.path) {
        const relativePath = asset.path.replace(/\\/g, '/');
        if (!component.referenceImages.includes(relativePath)) {
          updateComponent(component.id, {
            referenceImages: [...component.referenceImages, relativePath],
          });
        }
      }
    }
  }, [updateComponent, component.id, component.referenceImages]);

  return (
    <div
      onClick={handleClick}
      className={`
        rounded border p-2 cursor-pointer transition-colors text-xs
        ${isSelected
          ? 'border-blue-500 bg-blue-900/20'
          : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50'
        }
      `}
    >
      <div className="flex gap-2">
        {/* Thumbnail area -- drop zone for reference images */}
        <div
          className={`w-12 h-12 flex-shrink-0 rounded bg-zinc-800 border flex items-center justify-center overflow-hidden transition-colors
            ${isDragOver ? 'border-blue-500 bg-blue-900/20' : 'border-zinc-700'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {component.referenceImages.length > 0 ? (
            <div className="relative w-full h-full group/thumb">
              <RefImageThumb path={component.referenceImages[0]} className="w-full h-full object-cover" />
              <button
                onClick={(e) => handleRemoveRef(e, 0)}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity"
                title="Remove reference image"
              >
                <X size={10} className="text-white" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAddRefImage}
              className="w-full h-full flex items-center justify-center text-zinc-600 hover:text-zinc-400 transition-colors"
              title="Click to add reference image"
            >
              <ImagePlus size={14} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          {/* Name */}
          <input
            type="text"
            value={component.name}
            onChange={(e) => updateComponent(component.id, { name: e.target.value })}
            onClick={stopProp}
            className="w-full bg-transparent text-zinc-200 text-xs font-medium border-none outline-none focus:bg-zinc-800 rounded px-1 -mx-1"
          />

          {/* Dimensions + Format row */}
          <div className="flex items-center gap-1.5 text-zinc-400">
            <input
              type="number"
              value={component.width ?? ''}
              placeholder="W"
              onChange={(e) => updateComponent(component.id, { width: e.target.value ? Number(e.target.value) : null })}
              onClick={stopProp}
              className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-center"
            />
            <span className="text-zinc-600">x</span>
            <input
              type="number"
              value={component.height ?? ''}
              placeholder="H"
              onChange={(e) => updateComponent(component.id, { height: e.target.value ? Number(e.target.value) : null })}
              onClick={stopProp}
              className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-center"
            />
            <select
              value={component.format}
              onChange={(e) => updateComponent(component.id, { format: e.target.value as AssetFormat })}
              onClick={stopProp}
              className="w-16 shrink-0 bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-[10px] text-zinc-300"
            >
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <input
            type="text"
            value={component.notes}
            placeholder="Style notes..."
            onChange={(e) => updateComponent(component.id, { notes: e.target.value })}
            onClick={stopProp}
            className="w-full bg-transparent text-zinc-500 text-[10px] border-none outline-none focus:bg-zinc-800 rounded px-1 -mx-1 italic"
          />
        </div>

        {/* Actions column */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          {component.isCustom && (
            <button
              onClick={(e) => { e.stopPropagation(); removeCustomComponent(component.id); }}
              className="p-1 text-zinc-500 hover:text-red-400 rounded hover:bg-zinc-700"
              title="Remove custom component"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Golden variant indicator */}
      {component.deriveFrom && (
        <div className="mt-1 flex items-center gap-1 text-amber-500/70 text-[10px]">
          <Link size={10} />
          <span>derives from {component.deriveFrom}</span>
        </div>
      )}

      {/* Reference images row */}
      {component.referenceImages.length > 1 && (
        <div className="mt-1 flex gap-1 flex-wrap">
          {component.referenceImages.map((path, i) => (
            <div key={i} className="relative w-8 h-8 rounded overflow-hidden border border-zinc-700 group">
              <RefImageThumb path={path} className="w-full h-full object-cover" />
              <button
                onClick={(e) => handleRemoveRef(e, i)}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
              >
                <X size={10} className="text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
